import { TodoTask } from '../types/todo';
import { Order } from '../types/order';
import { getDaysSinceOrder } from './orderUtils';

/**
 * Gets the oldest order date from a task's associated orders
 * @param task - The task to check
 * @param orders - Array of all orders
 * @returns The oldest order date or null if no associated orders
 */
export function getOldestOrderDateForTask(task: TodoTask, orders: Order[]): string | null {
  let oldestOrderDate: string | null = null;

  // Check single orderId field
  if (task.orderId) {
    const order = orders.find(o => o.id === task.orderId);
    if (order) {
      oldestOrderDate = order.orderDate;
    }
  }

  // Check scheduledOrderIds field
  if (task.scheduledOrderIds && task.scheduledOrderIds.length > 0) {
    task.scheduledOrderIds.forEach(orderId => {
      const order = orders.find(o => o.id === orderId);
      if (order) {
        // If we don't have an oldest date yet, or this order is older
        if (!oldestOrderDate || order.orderDate < oldestOrderDate) {
          oldestOrderDate = order.orderDate;
        }
      }
    });
  }

  return oldestOrderDate;
}

/**
 * Gets the background color for a task based on the oldest associated order's age
 * @param task - The task to get color for
 * @param orders - Array of all orders
 * @returns CSS color string (rgba or hex)
 */
export function getTaskBackgroundColor(task: TodoTask, orders: Order[]): string {
  // If task is completed, use gray
  if (task.status === 'completed') {
    return '#F5F5F5';
  }

  const oldestOrderDate = getOldestOrderDateForTask(task, orders);

  // If no associated order found, use default yellow
  if (!oldestOrderDate) {
    return '#FFF9C4';
  }

  // Calculate days since oldest order
  const days = getDaysSinceOrder(oldestOrderDate);

  // Apply color scheme based on age (with transparency for readability)
  if (days <= 1) return 'rgba(76, 175, 80, 0.2)'; // Green (0-1 days)
  if (days <= 3) return 'rgba(139, 195, 74, 0.25)'; // Yellow-Green (2-3 days)
  if (days <= 5) return 'rgba(255, 193, 7, 0.3)'; // Yellow (4-5 days)
  if (days === 6) return 'rgba(255, 152, 0, 0.35)'; // Orange (6 days)
  return 'rgba(244, 67, 54, 0.3)'; // Red (7+ days)
}

/**
 * Gets the border color for a task (solid color matching the background)
 * @param task - The task to get border color for
 * @param orders - Array of all orders
 * @returns CSS color string (hex)
 */
export function getTaskBorderColor(task: TodoTask, orders: Order[]): string {
  // If task is completed, use gray
  if (task.status === 'completed') {
    return '#BDBDBD';
  }

  const oldestOrderDate = getOldestOrderDateForTask(task, orders);

  // If no associated order found, use default yellow
  if (!oldestOrderDate) {
    return '#FDD835';
  }

  // Calculate days since oldest order
  const days = getDaysSinceOrder(oldestOrderDate);

  // Apply solid border colors based on age
  if (days <= 1) return '#4CAF50'; // Green (0-1 days)
  if (days <= 3) return '#8BC34A'; // Yellow-Green (2-3 days)
  if (days <= 5) return '#FFC107'; // Yellow (4-5 days)
  if (days === 6) return '#FF9800'; // Orange (6 days)
  return '#F44336'; // Red (7+ days)
}

/**
 * Gets detailed information about a task's color calculation for debugging
 * @param task - The task to analyze
 * @param orders - Array of all orders
 * @returns Object with color calculation details
 */
export function getTaskColorDetails(task: TodoTask, orders: Order[]) {
  const oldestOrderDate = getOldestOrderDateForTask(task, orders);
  const days = oldestOrderDate ? getDaysSinceOrder(oldestOrderDate) : null;
  const backgroundColor = getTaskBackgroundColor(task, orders);
  const borderColor = getTaskBorderColor(task, orders);

  return {
    taskId: task.id,
    taskTitle: task.title,
    taskStatus: task.status,
    orderId: task.orderId,
    scheduledOrderIds: task.scheduledOrderIds,
    oldestOrderDate,
    daysSinceOldestOrder: days,
    backgroundColor,
    borderColor
  };
}
